/**
 * The demo's AI marketer reply (SKE-4166, phase 5) — the wiring.
 *
 * Registers the `setDemoAiReplyHandler` seam so that when the visitor leaves a
 * note on a post, "their" marketer answers in the same thread a moment later:
 *
 *   note → typing beat → reply
 *
 * WHAT THIS HOOK IS ALLOWED TO DO
 * -------------------------------
 * Write to the visitor's own localStorage snapshot, and nothing else. Both the
 * typing placeholder and the reply go in through `upsertDemoComment`, which is
 * the same client-only path every other demo mutation uses — see the hard
 * safety invariant at the top of demoApi.ts. The endpoint it calls is a
 * text-generation endpoint that writes nothing; the real collaboration comment
 * API is never touched, by this file or by the server it talks to.
 *
 * WHY IT NEEDS A REFRESH CALLBACK
 * -------------------------------
 * The approver's conversation thread renders from Homepage's `messages` state,
 * which is filled by `fetchComment`. Writing to the store is therefore not
 * enough to make the reply appear — the surrounding page has to be told to
 * re-read. Rather than teach the shared conversation components about the demo
 * (they stay completely untouched), Homepage hands this hook its own
 * `fetchCommentAsync` and the hook calls it at each beat. Outside demo mode the
 * hook registers nothing and the callback is never invoked.
 *
 * REDUCED MOTION
 * --------------
 * The typing beat *is* the animation here — a placeholder that appears and is
 * then replaced. Under `prefers-reduced-motion: reduce` it is skipped entirely
 * and the reply lands in one write, so the thread gains a message without
 * anything moving in and out underneath the reader.
 */

import { useCallback, useEffect, useRef } from 'react'

import { makeMarketerComment } from './curation'
import { fetchDemoAiReply, buildDemoAiReplyPayload } from './demoAiReply'
import { trackDemo } from './demoAnalytics'
import {
  clearDemoTypingComments,
  getDemoBrandSnapshot,
  getDemoPostCaption,
  setDemoAiReplyHandler,
  upsertDemoComment,
  type DemoAiReplyContext,
} from './demoApi'
import { getDemoSessionId, getDemoVertical } from './demoAttribution'
import { demoFormat, demoText } from './demoStrings'
import { isDemoMode } from './demoMode'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/**
 * How long the marketer "types" before the reply lands.
 *
 * Long enough to read as a person noticing the note, short enough that nobody
 * wonders whether it is stuck. The model call runs *during* this window rather
 * than after it, so a fast model costs the visitor nothing extra and a slow one
 * simply uses the beat up.
 */
const TYPING_BEAT_MS = 1100

/** Stable id per reply, derived from the note it answers. */
const replyIdFor = (commentId: string): string => `demo-reply-${commentId}`

export type DemoAiReplyRefresh = (
  reviewKey: string,
  postId: string
) => unknown | Promise<unknown>

/**
 * Wire the marketer's replies into the demo.
 *
 * Call unconditionally — outside demo mode this registers no handler and does
 * nothing, exactly like `useDemoBrandedSetting`.
 *
 * @param reviewKey the key Homepage is already rendering
 * @param refresh   Homepage's own comment re-fetch
 */
export const useDemoAiReply = (
  reviewKey: string,
  refresh: DemoAiReplyRefresh
): void => {
  const prefersReducedMotion = usePrefersReducedMotion()

  // Both change identity on most renders. Keeping them in refs means the
  // handler is registered once, on mount, instead of being torn down and
  // re-registered mid-flight — which would drop a reply already in the air.
  const refreshRef = useRef(refresh)
  const reviewKeyRef = useRef(reviewKey)
  const reducedMotionRef = useRef(prefersReducedMotion)

  refreshRef.current = refresh
  reviewKeyRef.current = reviewKey
  reducedMotionRef.current = prefersReducedMotion

  const notifyThread = useCallback(async (postId: string) => {
    try {
      await refreshRef.current(reviewKeyRef.current, postId)
    } catch {
      // The store already has the reply; a failed re-render is cosmetic and the
      // next thread open will pick it up.
    }
  }, [])

  const handleComment = useCallback(
    async ({ postId, commentId, text }: DemoAiReplyContext) => {
      const replyId = replyIdFor(commentId)
      const authorName = demoText('aiReplyAuthorName')
      const skipBeat = reducedMotionRef.current

      if (!skipBeat) {
        await upsertDemoComment(
          postId,
          makeMarketerComment({
            id: replyId,
            text: demoFormat('aiReplyTyping', { name: authorName }),
            createdAt: new Date().toISOString(),
            authorName,
            isTyping: true,
          })
        )
        await notifyThread(postId)
      }

      const brand = getDemoBrandSnapshot()

      // Run the request and the beat together: the visitor waits for whichever
      // is slower, not for both.
      const [result] = await Promise.all([
        fetchDemoAiReply(
          buildDemoAiReplyPayload({
            comment: text,
            caption: getDemoPostCaption(postId),
            brandName: brand?.name,
            vertical: getDemoVertical(),
            sessionId: getDemoSessionId(),
            commentId,
          })
        ),
        skipBeat
          ? Promise.resolve()
          : new Promise((resolve) => setTimeout(resolve, TYPING_BEAT_MS)),
      ])

      // Same id as the placeholder, so this replaces it rather than stacking
      // under it. `createdAt` is stamped now, not when the beat started, so the
      // reply sorts after the note it answers.
      await upsertDemoComment(
        postId,
        makeMarketerComment({
          id: replyId,
          text: result.reply,
          createdAt: new Date().toISOString(),
          authorName,
        })
      )
      await notifyThread(postId)

      trackDemo('demo-ai-reply-shown', {
        source: result.source,
        post_id: postId,
        // Deliberately no note text and no reply text: the note is a stranger's
        // free text about their own business, and the reply can quote it.
        reduced_motion: skipBeat,
      })
    },
    [notifyThread]
  )

  useEffect(() => {
    if (!isDemoMode()) {
      return
    }

    // A tab closed mid-beat leaves a persisted "typing…" placeholder. Clear any
    // on mount, before the thread has rendered anything, so a returning visitor
    // never meets a marketer who has been typing since their last visit. No
    // refresh is needed: nothing has read the comments yet.
    void clearDemoTypingComments().catch(() => undefined)

    setDemoAiReplyHandler((context) => {
      // The seam is fire-and-forget by design (`demoPostComment` must not wait
      // on a model), so the promise is deliberately not returned. Failures are
      // swallowed here rather than surfacing as an unhandled rejection — the
      // thread already has the visitor's own note either way.
      void handleComment(context).catch((error: unknown) => {
        console.warn('[external-review] demo AI reply failed', error)
      })
    })

    return () => {
      setDemoAiReplyHandler(null)
    }
  }, [handleComment])
}
